'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { Component } from 'react';
import UINumberInput from './UINumberInput';
import UICurrencyInput from './UICurrencyInput';
import UIButtonGroup from '../button/UIButtonGroup';
import UIButton from '../button/UIButton';
import UIIcon from '../icon/UIIcon';
import UITooltip from '../tooltip/UITooltip';
import Controllable from '../decorators/Controllable';
import { CALYPSO } from 'HubStyleTokens/colors';
import SyntheticEvent from '../core/SyntheticEvent';
import { callIfPossible } from '../core/Functions';
import classNames from 'classnames';
import { repeatUntilEventOnElement } from '../utils/Dom';
import { isLeftClick } from '../utils/DomEvents';
import ShareInput from '../decorators/ShareInput';
import { hidden } from '../utils/propTypes/decorators';

const getBaseValue = (value, placeholderValue) => value != null ? value : placeholderValue;

const isIncrementDisabled = (stepSize, baseValue, max, disabled) => {
  if (disabled) return true;
  if (max == null) return false;
  return !(baseValue + stepSize <= max);
};

const isDecrementDisabled = (stepSize, baseValue, min, disabled) => {
  if (disabled) return true;
  if (min == null) return false;
  return !(baseValue - stepSize >= min);
};

class UIStepperInput extends Component {
  constructor(...args) {
    super(...args);

    this.handleStep = step => {
      const {
        disabled,
        inputRef,
        max,
        min,
        onChange,
        onStep,
        placeholderValue,
        readOnly,
        value
      } = this.props;
      if (readOnly || disabled) return;
      if (inputRef.current) inputRef.current.focus();
      const newValue = getBaseValue(value, placeholderValue) + step;
      const fencedMin = min == null ? newValue : Math.max(newValue, min);
      const fencedValue = max == null ? fencedMin : Math.min(fencedMin, max); // "15 digits of precision ought to be enough for anyone" --Bill Gates

      const roundedValue = +parseFloat(fencedValue).toPrecision(15);
      onChange(SyntheticEvent(roundedValue));
      callIfPossible(onStep, SyntheticEvent(roundedValue));
    };

    this.increment = () => {
      if (this._freeze) return;
      const {
        stepSize
      } = this.props;
      this.handleStep(stepSize);
    };

    this.decrement = () => {
      if (this._freeze) return;
      const {
        stepSize
      } = this.props;
      this.handleStep(-stepSize);
    };

    this.handleUpArrowMouseDown = evt => {
      if (isLeftClick(evt)) {
        this.startMouseHold('increment');
      }
    };

    this.handleDownArrowMouseDown = evt => {
      if (isLeftClick(evt)) {
        this.startMouseHold('decrement');
      }
    };

    this.handleArrowMouseUp = () => {
      // Prevent click event from triggering an extra increment/decrement after click-and-hold
      if (this._repeatHandle && this._repeatHandle.count >= 1) {
        this._freeze = true;
        setTimeout(() => {
          this._freeze = 0;
        }, 0);
      }

      this._repeatHandle = null;
    };

    this.handleKeyDown = evt => {
      const {
        onKeyDown
      } = this.props;
      const {
        key
      } = evt;

      if (key === 'ArrowUp') {
        evt.preventDefault();
        this.increment();
      } else if (key === 'ArrowDown') {
        evt.preventDefault();
        this.decrement();
      }

      callIfPossible(onKeyDown, evt);
    };
  }

  componentWillUnmount() {
    if (this._repeatHandle != null) {
      this._repeatHandle.stop();
    }
  }

  startMouseHold(action) {
    this._repeatHandle = repeatUntilEventOnElement(this[action], 100, document.documentElement, ['mouseup']);
  }

  render() {
    const _this$props = this.props,
          {
      className,
      currency,
      disabled,
      disabledMaxButtonTooltip,
      disabledMaxButtonTooltipProps,
      disabledMinButtonTooltip,
      disabledMinButtonTooltipProps,
      inputClassName,
      max,
      min,
      placeholderValue,
      readOnly,
      stepSize,
      value
    } = _this$props,
          rest = _objectWithoutPropertiesLoose(_this$props, ["className", "currency", "disabled", "disabledMaxButtonTooltip", "disabledMaxButtonTooltipProps", "disabledMinButtonTooltip", "disabledMinButtonTooltipProps", "inputClassName", "max", "min", "onStep", "placeholderValue", "readOnly", "stepSize", "value"]);

    const classes = classNames('private-stepper-input', className);
    const inputClasses = classNames('private-stepper-input--input', inputClassName);
    const baseValue = getBaseValue(value, placeholderValue);
    const incrementDisabled = isIncrementDisabled(stepSize, baseValue, max, disabled);
    const decrementDisabled = isDecrementDisabled(stepSize, baseValue, min, disabled);
    const Input = currency ? UICurrencyInput : UINumberInput;
    return /*#__PURE__*/_jsxs("div", {
      className: classes,
      children: [/*#__PURE__*/_jsx(Input, Object.assign({}, rest, {
        "aria-valuemax": max,
        "aria-valuemin": min,
        "aria-valuenow": value,
        currency: currency,
        disabled: disabled,
        className: inputClasses,
        max: max,
        min: min,
        onKeyDown: this.handleKeyDown,
        readOnly: readOnly,
        role: "spinbutton",
        value: value
      })), readOnly ? null : /*#__PURE__*/_jsxs(UIButtonGroup, {
        className: "private-stepper-input--buttons",
        orientation: "vertical",
        children: [/*#__PURE__*/_jsx(UITooltip, Object.assign({
          placement: "top left"
        }, disabledMaxButtonTooltipProps, {
          title: disabledMaxButtonTooltip,
          disabled: !incrementDisabled,
          children: /*#__PURE__*/_jsx(UIButton, {
            "aria-hidden": true,
            disabled: incrementDisabled,
            onClick: this.increment,
            onMouseDown: this.handleUpArrowMouseDown,
            onMouseUp: this.handleArrowMouseUp,
            "data-action": "increment",
            use: "tertiary-light",
            size: "sm",
            tabIndex: -1,
            children: /*#__PURE__*/_jsx(UIIcon, {
              name: "up",
              color: !incrementDisabled ? CALYPSO : null
            })
          })
        })), /*#__PURE__*/_jsx(UITooltip, Object.assign({
          placement: "top left"
        }, disabledMinButtonTooltipProps, {
          title: disabledMinButtonTooltip,
          disabled: !decrementDisabled,
          children: /*#__PURE__*/_jsx(UIButton, {
            "aria-hidden": true,
            disabled: decrementDisabled,
            onClick: this.decrement,
            onMouseDown: this.handleDownArrowMouseDown,
            onMouseUp: this.handleArrowMouseUp,
            "data-action": "decrement",
            use: "tertiary-light",
            tabIndex: -1,
            size: "sm",
            children: /*#__PURE__*/_jsx(UIIcon, {
              name: "down",
              color: !decrementDisabled ? CALYPSO : null
            })
          })
        }))]
      })]
    });
  }

}

UIStepperInput.propTypes = Object.assign({}, UINumberInput.propTypes, {
  disabledMaxButtonTooltip: PropTypes.node,
  disabledMinButtonTooltip: PropTypes.node,
  disabledMaxButtonTooltipProps: PropTypes.object,
  disabledMinButtonTooltipProps: PropTypes.object,
  placeholderValue: hidden(PropTypes.number.isRequired),
  value: PropTypes.number,
  stepSize: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  onStep: PropTypes.func,
  inputClassName: PropTypes.string
});
UIStepperInput.defaultProps = Object.assign({}, UINumberInput.defaultProps, {
  disabledMaxButtonTooltipProps: {},
  disabledMinButtonTooltipProps: {},
  placeholderValue: 0,
  stepSize: 1
});
UIStepperInput.displayName = 'UIStepperInput';
export default ShareInput(Controllable(UIStepperInput));